/**
 * Admin Users — V2.N.9 (refonte centrée identité)
 *
 * Vue **par utilisateur réel** (pas par device). Une identité regroupe :
 *   - Un compte Supabase
 *   - Des devices anonymes qui partagent au moins un wallet
 *     (= la même personne sur 2 fenêtres / 2 appareils)
 *   - Transitivité union-find : A↔B et B↔C ⇒ même personne
 *
 * Lit /api/admin/identities. Master-detail :
 *   - 5 cartes synthèse (identités, comptes, anonymes, wallets distincts,
 *     portfolios distincts)
 *   - Filtre tous / comptes / anonymes
 *   - Liste : pays, # devices, # wallets, # portfolios, # modules, vu
 *   - Panneau détail : devices liés, wallets uniques, portfolios + tokens,
 *     pages activées + visitées
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchAdminAction } from '../lib/fetchAdminAction';
import { relTime, absTime } from '../lib/dateHelpers';
import { adminPageStyle, adminCardStyle, adminTableStyle, adminThStyle, adminTdStyle, adminCodeStyle } from '../lib/adminStyles';
import '../layout/AdminLayout.css';

type Filter = 'all' | 'accounts' | 'anonymous';

interface Overview {
  identities_total: number;
  identities_accounts: number;
  identities_anonymous: number;
  wallets_total: number;
  portfolios_total: number;
}

interface IdentityRow {
  identity_key: string;
  has_account: boolean;
  email: string | null;
  device_count: number;
  countries: string[];
  wallet_count: number;
  portfolio_count: number;
  page_module_count: number;
  first_seen: string;
  last_seen: string;
}

interface DeviceRow {
  device_id: string;
  last_seen: string;
  country: string | null;
  city: string | null;
  user_agent: string | null;
  locale: string | null;
  currency: string | null;
  user_mode: string | null;
}

interface WalletRow {
  chain: string;
  address: string;
  label: string | null;
  source: string;
}

interface PortfolioRow {
  portfolio_id: string;
  name: string | null;
  token_count: number;
  position_count: number;
  tokens: Array<{ coin_id: string; symbol?: string }>;
}

interface IdentityDetail extends IdentityRow {
  devices: DeviceRow[];
  wallets: WalletRow[];
  portfolios: PortfolioRow[];
  enabled_modules: Record<string, boolean>;
  visited_pages: string[];
}

const ENDPOINT = '/api/admin/identities';

export function Users() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [rows, setRows] = useState<IdentityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<IdentityDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const o = await fetchAdminAction<Overview>(ENDPOINT, 'overview');
      if (!cancelled) setOverview(o);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const list = await fetchAdminAction<IdentityRow[]>(ENDPOINT, 'list', { filter });
      if (!cancelled) {
        setRows(list || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const openDetail = useCallback(async (key: string) => {
    setSelected(key);
    setDetailLoading(true);
    setDetail(null);
    const d = await fetchAdminAction<IdentityDetail>(ENDPOINT, 'detail', { identity: key });
    setDetail(d);
    setDetailLoading(false);
  }, []);

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Utilisateurs</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          Une ligne = une personne (compte ou cluster d'appareils anonymes partageant des wallets).
        </div>
      </header>

      {/* Cartes synthèse */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <StatCard label="Utilisateurs total" value={overview?.identities_total ?? '—'} accent="#0969da" />
        <StatCard label="Avec compte" value={overview?.identities_accounts ?? '—'} accent="#0e9f6e" sub="Inscrits sur la plateforme" />
        <StatCard label="Sans compte" value={overview?.identities_anonymous ?? '—'} accent="#d97706" sub="Identifiés par wallets" />
        <StatCard label="Wallets distincts" value={overview?.wallets_total ?? '—'} accent="#6366f1" />
        <StatCard label="Portfolios distincts" value={overview?.portfolios_total ?? '—'} accent="#8b5cf6" />
      </div>

      {/* Filtre */}
      <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '14px' }}>
        {(['all', 'accounts', 'anonymous'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 14px',
              background: filter === f ? 'var(--accent)' : 'transparent',
              color: filter === f ? '#fff' : 'var(--text)',
              border: 'none',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {f === 'all' ? 'Tous' : f === 'accounts' ? 'Comptes' : 'Anonymes'}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : '1fr', gap: '16px' }}>
        {/* Liste */}
        <section style={adminCardStyle}>
          {loading ? (
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
              Aucun utilisateur pour ce filtre. Les données apparaissent dès qu'un visiteur ouvre l'app (le mirroring local→DB est asynchrone).
            </div>
          ) : (
            <table style={adminTableStyle}>
              <thead>
                <tr>
                  <th style={adminThStyle}>Type</th>
                  <th style={adminThStyle}>Identité</th>
                  <th style={adminThStyle}>Pays</th>
                  <th style={adminThStyle}>Appareils</th>
                  <th style={adminThStyle}>Wallets</th>
                  <th style={adminThStyle}>Portf.</th>
                  <th style={adminThStyle}>Pages</th>
                  <th style={adminThStyle}>Vu</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.identity_key}
                    onClick={() => openDetail(r.identity_key)}
                    style={{ cursor: 'pointer', background: selected === r.identity_key ? 'rgba(9,105,218,0.06)' : 'transparent' }}
                  >
                    <td style={adminTdStyle}><Badge account={r.has_account} /></td>
                    <td style={adminTdStyle}>
                      {r.has_account ? (
                        <span style={{ fontWeight: 600 }}>{r.email || r.identity_key.slice(5, 13)}</span>
                      ) : (
                        <code style={adminCodeStyle}>{r.identity_key.replace('device:', '').slice(0, 12)}…</code>
                      )}
                    </td>
                    <td style={adminTdStyle}>{r.countries.join(', ') || '—'}</td>
                    <td style={adminTdStyle}>{r.device_count}</td>
                    <td style={adminTdStyle}>{r.wallet_count}</td>
                    <td style={adminTdStyle}>{r.portfolio_count}</td>
                    <td style={adminTdStyle}>{r.page_module_count}</td>
                    <td style={adminTdStyle}>{r.last_seen ? relTime(r.last_seen) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Détail */}
        {selected && (
          <aside style={{ ...adminCardStyle, position: 'sticky', top: '16px', alignSelf: 'start', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>Détail utilisateur</h2>
              <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', color: 'var(--muted)', fontSize: '12px' }}>✕</button>
            </div>
            {detailLoading ? (
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>
            ) : !detail ? (
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Indisponible.</div>
            ) : (
              <DetailPanel detail={detail} />
            )}
          </aside>
        )}
      </div>
    </main>
  );
}

function DetailPanel({ detail }: { detail: IdentityDetail }) {
  return (
    <div style={{ fontSize: '12px' }}>
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <Badge account={detail.has_account} />
          {detail.has_account ? (
            <strong>{detail.email || 'compte sans email'}</strong>
          ) : (
            <code style={adminCodeStyle}>{detail.identity_key}</code>
          )}
        </div>
        <Field label="Première activité" value={detail.first_seen ? absTime(detail.first_seen) : '—'} />
        <Field label="Dernière activité" value={detail.last_seen ? absTime(detail.last_seen) : '—'} />
        <Field label="Pays" value={detail.countries.join(', ') || '—'} />
        <Field label="Appareils liés" value={String(detail.devices.length)} />
        <Field label="Wallets uniques" value={String(detail.wallets.length)} />
        <Field label="Portfolios" value={String(detail.portfolios.length)} />
      </div>

      <Section title={`Wallets (${detail.wallets.length})`}>
        {detail.wallets.length === 0 ? <Empty /> : (
          <ul style={listStyle}>
            {detail.wallets.map((w, i) => (
              <li key={i} style={liStyle}>
                <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: 'var(--accent)' }}>{w.chain}</span>{' '}
                <code style={adminCodeStyle}>{w.address}</code>
                {w.label && <span style={{ color: 'var(--muted)', fontSize: '11px' }}> · {w.label}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={`Portfolios (${detail.portfolios.length})`}>
        {detail.portfolios.length === 0 ? <Empty /> : detail.portfolios.map((p) => (
          <div key={p.portfolio_id} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: 600 }}>
              {p.name || p.portfolio_id} — {p.token_count} tokens, {p.position_count} positions
            </div>
            <div style={{ color: 'var(--muted)', fontSize: '11px' }}>
              {p.tokens.map((t) => t.symbol || t.coin_id).join(', ') || '—'}
            </div>
          </div>
        ))}
      </Section>

      <Section title={`Pages activées (${Object.values(detail.enabled_modules).filter(Boolean).length})`}>
        {!detail.enabled_modules || Object.keys(detail.enabled_modules).length === 0 ? <Empty /> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {Object.entries(detail.enabled_modules).map(([mod, on]) => (
              <span key={mod} style={{
                fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                background: on ? 'rgba(14,159,110,0.15)' : 'rgba(105,115,134,0.12)',
                color: on ? '#0e9f6e' : 'var(--muted)',
                textDecoration: on ? 'none' : 'line-through',
              }}>{mod}</span>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Appareils liés (${detail.devices.length})`}>
        {detail.devices.length === 0 ? <Empty /> : (
          <ul style={listStyle}>
            {detail.devices.map((d, i) => (
              <li key={i} style={liStyle}>
                <code style={adminCodeStyle}>{d.device_id.slice(0, 12)}…</code>{' '}
                <span style={{ color: 'var(--muted)' }}>· vu {relTime(d.last_seen)}</span>
                {(d.city || d.country) && (
                  <span style={{ color: 'var(--muted)' }}> · {[d.city, d.country].filter(Boolean).join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: { label: string; value: number | string; sub?: string; accent: string }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
      <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 700, marginTop: '4px', color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

function Badge({ account }: { account: boolean }) {
  return (
    <span style={{
      fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, textTransform: 'uppercase',
      background: account ? 'rgba(14,159,110,0.15)' : 'rgba(217,119,6,0.15)',
      color: account ? '#0e9f6e' : '#d97706',
    }}>{account ? 'Compte' : 'Anonyme'}</span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', marginTop: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--muted)', marginBottom: '6px' }}>{title}</div>
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>—</div>;
}

// Styles locaux spécifiques au panneau détail.
const listStyle: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0 };
const liStyle: React.CSSProperties = { padding: '3px 0', borderBottom: '1px solid var(--border)' };
